import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Car, MapPin, X, GripVertical, ArrowRight, Clock, AlertCircle, ChevronUp, ChevronDown, Plus, Plane, Building, Train, Home, Search, Loader2, ChevronLeft, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { searchLocations } from '../../services/urbanrideService';
import { formatDateTimePreserveTimezone } from '../../utils/dateTimeUtils';
import axios from 'axios';

/**
 * UrbanrideBookingSummary - Right panel component for dynamic Urbanride booking workflow
 *
 * Shows the current itinerary as users add locations:
 * - First item = Pickup (green)
 * - Last item = Dropoff (red)
 * - Intermediate items = Stops (blue)
 *
 * TBD items can be reordered via drag-and-drop
 * Fixed items are ordered chronologically
 *
 * Same location can appear multiple times (e.g., hotel -> meeting -> hotel)
 * Removal is done by index to support duplicate entries
 */
const UrbanrideBookingSummary = ({
  selectedComponents = [],
  onRemoveComponent, // Called with selectionIndex to remove by position
  onReorderComponents,
  onStartBooking,
  onCancel,
  onAddManualLocation, // Callback to add a manually selected location
  tripExecutives = [] // Executives assigned to this trip (for Favorites)
}) => {
  // Manual location addition state
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [locationType, setLocationType] = useState(null); // 'airport', 'fbo', 'train_station', 'address'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState(null); // For FBO selection
  const searchTimeoutRef = useRef(null);

  // Address search state (uses backend proxy for Google Places)
  const [addressResults, setAddressResults] = useState([]);
  const [isAddressSearching, setIsAddressSearching] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const addressSearchTimeoutRef = useRef(null);

  // Favorites state - saved addresses from trip executives
  const [showFavorites, setShowFavorites] = useState(false);

  // Helper to check if a component has a fixed time (not TBD)
  const isFixedComponent = (component) => {
    const compData = component?.component_data || {};
    const startDateTime = component?.start_datetime;

    // Check TBD flags
    const dateIsTBD = component?.date_is_tbd === true || compData.date_is_tbd === true;
    const timeIsTBD = component?.time_is_tbd === true || compData.time_is_tbd === true;

    if (dateIsTBD || timeIsTBD) return false;

    // If has actual datetime and no TBD flags, it's fixed
    if (startDateTime && typeof startDateTime === 'string' && startDateTime.trim() !== '') {
      if (startDateTime.includes('-TBD-TIME')) return false;
      return true;
    }

    return false;
  };

  // Helper to extract location name from component
  // role: 'pickup' = arriving passenger, 'dropoff' = departing passenger, 'stop' = intermediate
  const getLocationName = (component, role = null) => {
    const cd = component?.component_data || {};
    const componentType = component?.component_type || component?.originalComponent?.component_type;

    // Manual location - use the stored location name
    if (component?.isManualLocation || componentType === 'manual_location') {
      return cd.location_name || component.title || 'Manual Location';
    }

    // Flight leg - use arrival/departure airport based on role
    if (component?.isFlightLeg && component?.flightLeg) {
      const leg = component.flightLeg;
      const arrCode = leg.arrival_airport || leg.arrival_airport_code || '';
      const depCode = leg.departure_airport || leg.departure_airport_code || '';

      // For pickup role (passenger arriving on this flight), use arrival airport
      // For dropoff role (passenger catching this flight), use departure airport
      if (role === 'pickup') {
        return arrCode ? `${arrCode} Airport` : (depCode ? `${depCode} Airport` : component.title);
      } else if (role === 'dropoff') {
        return depCode ? `${depCode} Airport` : (arrCode ? `${arrCode} Airport` : component.title);
      }
      // Default: prefer arrival for display
      return arrCode ? `${arrCode} Airport` : (depCode ? `${depCode} Airport` : component.title);
    }

    // Hotel
    if (componentType === 'hotel') {
      return cd.hotel_name || component.title || 'Hotel';
    }

    // Restaurant
    if (componentType === 'restaurant') {
      return cd.restaurant_name || component.title || 'Restaurant';
    }

    // Meeting
    if (componentType === 'meeting') {
      return component.title || cd.meeting_title || 'Meeting';
    }

    // Event
    if (componentType === 'event') {
      return component.title || cd.event_name || 'Event';
    }

    return component.title || 'Location';
  };

  // Helper to get location address/details
  const getLocationDetails = (component) => {
    const cd = component?.component_data || {};
    const componentType = component?.component_type || component?.originalComponent?.component_type;

    // Manual location - use the stored location details
    if (component?.isManualLocation || componentType === 'manual_location') {
      return cd.location_details || component.location || null;
    }

    if (component?.isFlightLeg && component?.flightLeg) {
      const leg = component.flightLeg;
      const airline = leg.airline_name || leg.airline || '';
      const flightNum = leg.flight_number || '';
      if (airline && flightNum) return `${airline} ${flightNum}`;
      return null;
    }

    if (componentType === 'hotel') {
      return cd.hotel_address || cd.address || component.location;
    }

    if (componentType === 'restaurant') {
      return cd.address || component.location;
    }

    if (componentType === 'meeting') {
      return cd.meeting_location || cd.location || component.location;
    }

    if (componentType === 'event') {
      return cd.event_location || cd.location || component.location;
    }

    return component.location;
  };

  // Helper to get datetime for display - preserves original timezone, doesn't convert to browser TZ
  // For flights: pickup = arrival time (end_datetime), dropoff = departure time (start_datetime)
  const getDateTime = (component, role = null) => {
    // Determine which datetime to use based on component type and role
    let isoString = component?.start_datetime;

    // For flights, use arrival time (end_datetime) when used as pickup,
    // and departure time (start_datetime) when used as dropoff
    const isFlight = component?.component_type === 'commercial_flight' ||
                     component?.component_type === 'private_flight' ||
                     component?.isFlightLeg;

    if (isFlight && role === 'pickup') {
      // Flight arrival - use end_datetime
      isoString = component?.end_datetime || component?.start_datetime;
    } else if (isFlight && role === 'dropoff') {
      // Flight departure - use start_datetime (already default)
      isoString = component?.start_datetime;
    }

    if (!isoString) return null;
    try {
      // Use timezone-preserving format to avoid browser timezone conversion
      // This extracts the time directly from the ISO string
      const match = String(isoString).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);

      if (!match) return null;

      const [, year, month, day, hours, minutes] = match;

      // Create a date object just to get the day of week (use noon to avoid DST issues)
      const dateForDow = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
      const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dateForDow.getDay()];
      const monthName = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parseInt(month) - 1];

      // Convert 24h to 12h format with AM/PM
      let hour12 = parseInt(hours);
      const ampm = hour12 >= 12 ? 'PM' : 'AM';
      if (hour12 === 0) hour12 = 12;
      else if (hour12 > 12) hour12 -= 12;

      return `${dayOfWeek}, ${monthName} ${parseInt(day)} at ${hour12}:${minutes} ${ampm}`;
    } catch {
      return null;
    }
  };

  // Helper to get component type icon color
  const getTypeColor = (component) => {
    const componentType = component?.component_type || component?.originalComponent?.component_type;

    if (component?.isFlightLeg) return 'text-ui-link';

    // Manual location colors based on location type
    if (component?.isManualLocation || componentType === 'manual_location') {
      const locType = component?.manualLocationType || component?.component_data?.location_type;
      switch (locType) {
        case 'airport': return 'text-ui-link';
        case 'fbo': return 'text-ui-link';
        case 'train_station': return 'text-ui-amber-fg';
        case 'address': return 'text-ui-teal-fg';
        case 'as_directed': return 'text-ui-link';
        default: return 'text-ui-ink-3';
      }
    }

    switch (componentType) {
      case 'hotel': return 'text-ui-indigo-fg';
      case 'restaurant': return 'text-ui-orange-fg';
      case 'meeting': return 'text-ui-green-fg';
      case 'event': return 'text-ui-violet-fg';
      default: return 'text-ui-ink-3';
    }
  };

  // Process and order the selected components
  // Always maintain selection order - user specifies the route order explicitly
  // The order they click "Add to Itinerary" is the order they want to travel
  const orderedComponents = useMemo(() => {
    if (selectedComponents.length === 0) return [];

    return selectedComponents.map((comp, selectionIndex) => ({
      ...comp,
      selectionIndex,
      isFixed: isFixedComponent(comp)
    }));
  }, [selectedComponents]);

  // Determine roles: first = pickup, last = dropoff, middle = stops
  const getItemRole = (index, total) => {
    if (total === 0) return 'none';
    if (index === 0) return 'pickup';
    if (index === total - 1) return 'dropoff';
    return 'stop';
  };

  const getRoleBadge = (role, index) => {
    switch (role) {
      case 'pickup':
        return (
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-ui-green-bg text-ui-green-fg text-xs font-medium">
            <div className="w-4 h-4 rounded-full bg-ui-green-strong flex items-center justify-center text-ui-on-primary text-[10px] font-bold">P</div>
            Pickup
          </div>
        );
      case 'dropoff':
        return (
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-ui-red-bg text-ui-red-fg text-xs font-medium">
            <div className="w-4 h-4 rounded-full bg-ui-red-strong flex items-center justify-center text-ui-on-primary text-[10px] font-bold">D</div>
            Dropoff
          </div>
        );
      case 'stop':
        return (
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-ui-blue-bg text-ui-link text-xs font-medium">
            <div className="w-4 h-4 rounded-full bg-ui-primary flex items-center justify-center text-ui-on-primary text-[10px] font-bold">{index}</div>
            Stop {index}
          </div>
        );
      default:
        return null;
    }
  };

  // Handle reordering of TBD items
  const handleReorder = (newOrder) => {
    if (onReorderComponents) {
      onReorderComponents(newOrder);
    }
  };

  // Handle moving item up/down
  const moveItem = (index, direction) => {
    if (!onReorderComponents) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= selectedComponents.length) return;

    const newOrder = [...selectedComponents];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    onReorderComponents(newOrder);
  };

  // Collect saved addresses from all trip executives
  const favoriteAddresses = useMemo(() => {
    const addresses = [];
    if (Array.isArray(tripExecutives)) {
      tripExecutives.forEach(exec => {
        if (exec.saved_addresses && Array.isArray(exec.saved_addresses)) {
          exec.saved_addresses.forEach(addr => {
            addresses.push({
              ...addr,
              executiveName: `${exec.first_name || ''} ${exec.last_name || ''}`.trim(),
              executiveId: exec.id
            });
          });
        }
      });
    }
    return addresses;
  }, [tripExecutives]);

  // Location type options for manual addition
  const locationTypes = [
    // Show Favorites first if there are any saved addresses from executives
    ...(favoriteAddresses.length > 0 ? [{ value: 'favorites', label: 'Favorites', icon: Star, description: 'Saved addresses from executives' }] : []),
    { value: 'airport', label: 'Airport', icon: Plane, description: 'Commercial or private airport' },
    { value: 'fbo', label: 'FBO', icon: Building, description: 'Fixed Base Operator' },
    { value: 'train_station', label: 'Train Station', icon: Train, description: 'Rail station' },
    { value: 'address', label: 'Address', icon: Home, description: 'Any address location' },
    { value: 'as_directed', label: 'As Directed', icon: Car, description: 'Passenger directs driver' }
  ];

  // Helper to extract location coordinates from selected components for location biasing
  const getLocationBias = useCallback(() => {
    if (selectedComponents.length === 0) return null;

    // Try to get coordinates from the first selected component
    for (const comp of selectedComponents) {
      // Check for manual location with geometry
      if (comp.manualLocationData?.geometry?.location) {
        const loc = comp.manualLocationData.geometry.location;
        return `${loc.lat},${loc.lng}`;
      }

      // Check for flight leg with airport coordinates
      if (comp.isFlightLeg && comp.flightLeg) {
        const leg = comp.flightLeg;
        // Use arrival airport coordinates if available
        if (leg.arrival_lat && leg.arrival_lng) {
          return `${leg.arrival_lat},${leg.arrival_lng}`;
        }
        if (leg.departure_lat && leg.departure_lng) {
          return `${leg.departure_lat},${leg.departure_lng}`;
        }
      }

      // Check component_data for coordinates
      const cd = comp.component_data || {};
      if (cd.latitude && cd.longitude) {
        return `${cd.latitude},${cd.longitude}`;
      }
      if (cd.lat && cd.lng) {
        return `${cd.lat},${cd.lng}`;
      }

      // Check for hotel coordinates
      if (cd.hotel_latitude && cd.hotel_longitude) {
        return `${cd.hotel_latitude},${cd.hotel_longitude}`;
      }
    }

    return null;
  }, [selectedComponents]);

  // Handle address search using backend proxy (Google Places API)
  const handleAddressSearch = useCallback(async (query) => {
    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
    }

    if (!query || query.length < 3) {
      setAddressResults([]);
      setIsAddressSearching(false);
      return;
    }

    setIsAddressSearching(true);

    addressSearchTimeoutRef.current = setTimeout(async () => {
      try {
        // Build params - no types restriction to allow hotels/establishments/addresses
        const params = { query: query };

        // Add location bias if we have selected components with coordinates
        const locationBias = getLocationBias();
        if (locationBias) {
          params.location = locationBias;
          params.radius = 50000; // 50km radius
        }

        // Use the backend proxy endpoint for Google Places autocomplete
        const response = await axios.get('/places/autocomplete', { params });

        if (response.data && response.data.predictions) {
          const results = response.data.predictions.map(prediction => ({
            id: prediction.place_id,
            name: prediction.structured_formatting?.main_text || prediction.description.split(',')[0],
            details: prediction.description,
            placeId: prediction.place_id,
            type: 'address'
          }));
          setAddressResults(results);
        } else {
          setAddressResults([]);
        }
      } catch (error) {
        console.error('Address search failed:', error);
        setAddressResults([]);
      } finally {
        setIsAddressSearching(false);
      }
    }, 300);
  }, [getLocationBias]);

  // Handle address selection
  const handleSelectAddress = useCallback((address) => {
    // Create a manual component from the selected address
    const manualComponent = {
      id: `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      component_type: 'manual_location',
      title: address.name,
      location: address.details,
      isManualLocation: true,
      manualLocationType: 'address',
      manualLocationData: address,
      component_data: {
        location_type: 'address',
        location_id: address.id,
        location_name: address.name,
        location_details: address.details,
        place_id: address.placeId
      },
      date_is_tbd: true,
      time_is_tbd: true
    };

    if (onAddManualLocation) {
      onAddManualLocation(manualComponent);
    }

    // Reset the form
    setShowAddLocation(false);
    setLocationType(null);
    setSearchQuery('');
    setSearchResults([]);
    setAddressQuery('');
    setAddressResults([]);
  }, [onAddManualLocation]);

  // Handle location search with debounce
  const handleLocationSearch = useCallback(async (query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query || query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const locationQuery = { input: query };

        // Set the type filter based on selected location type
        if (locationType === 'airport') {
          locationQuery.type = 'airport';
        } else if (locationType === 'fbo') {
          // For FBO, first we need to search for airports, then FBOs at that airport
          if (!selectedAirport) {
            locationQuery.type = 'airport';
          } else {
            locationQuery.type = 'fbo';
            locationQuery.airport = selectedAirport.id;
          }
        } else if (locationType === 'train_station') {
          locationQuery.type = 'train-station';  // Urbanride API expects 'train-station' with hyphen
        }
        // For 'address', no type filter (searches all types including addresses)

        const result = await searchLocations(locationQuery);
        setSearchResults(result.locations || []);
      } catch (error) {
        console.error('Location search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [locationType, selectedAirport]);

  // Handle location selection
  const handleSelectLocation = useCallback((location) => {
    if (locationType === 'fbo' && !selectedAirport) {
      // First step for FBO: select airport
      setSelectedAirport(location);
      setSearchQuery('');
      setSearchResults([]);
      return;
    }

    // Create a manual component from the selected location
    // For FBOs, include the airport code separately from the FBO id
    const locationDataWithAirport = locationType === 'fbo' && selectedAirport
      ? { ...location, airportCode: selectedAirport.id }
      : location;

    const manualComponent = {
      id: `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      component_type: 'manual_location',
      title: location.name,
      location: location.details || location.name,
      isManualLocation: true,
      manualLocationType: locationType,
      manualLocationData: locationDataWithAirport,
      component_data: {
        location_type: locationType,
        location_id: location.id,
        location_name: location.name,
        location_details: location.details,
        // For FBOs, store the airport code separately
        ...(locationType === 'fbo' && selectedAirport && { airport_code: selectedAirport.id })
      },
      // Mark as TBD since manual locations don't have fixed times
      date_is_tbd: true,
      time_is_tbd: true
    };

    if (onAddManualLocation) {
      onAddManualLocation(manualComponent);
    }

    // Reset the add location form
    setShowAddLocation(false);
    setLocationType(null);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedAirport(null);
  }, [locationType, selectedAirport, onAddManualLocation]);

  // Handle "As Directed" selection - immediately add the component without search
  const handleAsDirectedSelection = useCallback(() => {
    const manualComponent = {
      id: `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      component_type: 'manual_location',
      title: 'As Directed',
      location: 'As Directed',
      isManualLocation: true,
      manualLocationType: 'as_directed',
      manualLocationData: {
        id: '',
        name: 'As Directed',
        details: 'Passenger/s will direct to final destination.',
        type: 'as_directed'
      },
      component_data: {
        location_type: 'as_directed',
        location_id: '',
        location_name: 'As Directed',
        location_details: 'Passenger/s will direct to final destination.'
      },
      // Mark as TBD since as_directed locations don't have fixed times
      date_is_tbd: true,
      time_is_tbd: true
    };

    if (onAddManualLocation) {
      onAddManualLocation(manualComponent);
    }

    // Reset the add location form
    setShowAddLocation(false);
    setLocationType(null);
  }, [onAddManualLocation]);

  // Handle favorite address selection - add from executive's saved addresses
  const handleSelectFavorite = useCallback((favorite) => {
    const addr = favorite.address || {};
    const fullAddress = [
      addr.addressLine1,
      addr.city,
      addr.state_province,
      addr.postalCode,
      addr.countryName
    ].filter(Boolean).join(', ');

    const manualComponent = {
      id: `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      component_type: 'manual_location',
      title: favorite.label || 'Saved Address',
      location: fullAddress,
      isManualLocation: true,
      manualLocationType: 'address',
      manualLocationData: {
        id: favorite.place_id || '',
        name: favorite.label || 'Saved Address',
        details: fullAddress,
        placeId: favorite.place_id || '',
        type: 'address'
      },
      component_data: {
        location_type: 'address',
        location_id: favorite.place_id || '',
        location_name: favorite.label || 'Saved Address',
        location_details: fullAddress,
        place_id: favorite.place_id || ''
      },
      date_is_tbd: true,
      time_is_tbd: true
    };

    if (onAddManualLocation) {
      onAddManualLocation(manualComponent);
    }

    // Reset the add location form
    setShowAddLocation(false);
    setLocationType(null);
  }, [onAddManualLocation]);

  // Reset add location form
  const resetAddLocationForm = useCallback(() => {
    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
    }
    setShowAddLocation(false);
    setLocationType(null);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedAirport(null);
    setAddressQuery('');
    setAddressResults([]);
  }, []);

  // Go back in the add location flow
  const handleBackInAddLocation = useCallback(() => {
    if (selectedAirport) {
      // Go back from FBO search to airport search
      setSelectedAirport(null);
      setSearchQuery('');
      setSearchResults([]);
    } else if (locationType) {
      // Clear address search if going back from address
      if (locationType === 'address') {
        if (addressSearchTimeoutRef.current) {
          clearTimeout(addressSearchTimeoutRef.current);
        }
        setAddressQuery('');
        setAddressResults([]);
      }
      // Go back to location type selection
      setLocationType(null);
      setSearchQuery('');
      setSearchResults([]);
    } else {
      // Close the add location panel
      setShowAddLocation(false);
    }
  }, [selectedAirport, locationType]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (addressSearchTimeoutRef.current) {
        clearTimeout(addressSearchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="bg-ui-surface rounded-xl border border-ui-line shadow-lg flex flex-col h-full"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-ui-line bg-gradient-to-r from-ui-blue-strong to-ui-indigo-strong rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Car className="w-5 h-5 text-ui-on-primary" />
            <h3 className="text-sm font-semibold text-ui-on-primary">Booking Itinerary</h3>
          </div>
          <button
            onClick={onCancel}
            className="text-ui-on-primary/80 hover:text-ui-on-primary hover:bg-ui-on-primary/20 p-1 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedComponents.length === 0 ? (
          <div className="space-y-4">
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-ui-hover rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-8 h-8 text-ui-muted" />
              </div>
              <p className="text-sm text-ui-ink-3 mb-2">No locations selected</p>
              <p className="text-xs text-ui-muted">
                Click "Add to Itinerary" on timeline items or add a location manually
              </p>
            </div>

            {/* Add Manual Location Section - always visible */}
            <div className="pt-3 border-t border-ui-line">
              <AnimatePresence mode="wait">
                {!showAddLocation ? (
                  <motion.button
                    key="add-button-empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowAddLocation(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-ui-hover hover:bg-ui-line text-ui-ink-2 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm font-medium">Add Manual Location</span>
                  </motion.button>
                ) : (
                  <motion.div
                    key="add-panel-empty"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-3"
                  >
                    {/* Header with back button */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={handleBackInAddLocation}
                        className="flex items-center gap-1 text-sm text-ui-ink-3 hover:text-ui-ink"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        {selectedAirport ? 'Back to Airport' : locationType ? 'Back' : 'Cancel'}
                      </button>
                      <button
                        onClick={resetAddLocationForm}
                        className="p-1 text-ui-muted hover:text-ui-ink-3"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Location Type Selection */}
                    {!locationType && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">Select location type:</p>
                        <div className="grid grid-cols-2 gap-2">
                          {locationTypes.map((type) => {
                            const IconComponent = type.icon;
                            return (
                              <button
                                key={type.value}
                                onClick={() => {
                                  if (type.value === 'as_directed') {
                                    handleAsDirectedSelection();
                                  } else if (type.value === 'favorites') {
                                    setLocationType('favorites');
                                  } else {
                                    setLocationType(type.value);
                                  }
                                }}
                                className={`flex flex-col items-center gap-1.5 p-3 bg-ui-surface border border-ui-line rounded-lg hover:border-ui-primary hover:bg-ui-blue-bg transition-all ${type.value === 'as_directed' ? 'col-span-2' : ''} ${type.value === 'favorites' ? 'col-span-2 border-ui-amber-line bg-ui-amber-bg/30 hover:border-ui-amber-solid hover:bg-ui-amber-bg' : ''}`}
                              >
                                <IconComponent className={`w-5 h-5 ${type.value === 'favorites' ? 'text-ui-amber-fg' : 'text-ui-ink-3'}`} />
                                <span className={`text-xs font-medium ${type.value === 'favorites' ? 'text-ui-amber-fg' : 'text-ui-ink-2'}`}>{type.label}</span>
                                {(type.value === 'as_directed' || type.value === 'favorites') && (
                                  <span className="text-[10px] text-ui-muted">{type.description}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Favorites Selection */}
                    {locationType === 'favorites' && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">Select from saved addresses:</p>
                        <div className="max-h-48 overflow-y-auto border border-ui-line rounded-lg divide-y divide-ui-divider">
                          {favoriteAddresses.map((favorite, index) => {
                            const addr = favorite.address || {};
                            return (
                              <button
                                key={favorite.id || index}
                                onClick={() => handleSelectFavorite(favorite)}
                                className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-ui-amber-bg transition-colors"
                              >
                                <div className="flex-shrink-0 w-7 h-7 bg-ui-amber-bg rounded-full flex items-center justify-center mt-0.5">
                                  <Star className="w-3.5 h-3.5 text-ui-amber-fg" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-ui-ink truncate">
                                    {favorite.label}
                                  </p>
                                  <p className="text-xs text-ui-muted truncate">
                                    {addr.addressLine1}
                                    {addr.city && `, ${addr.city}`}
                                  </p>
                                  <p className="text-[10px] text-ui-muted truncate">
                                    {favorite.executiveName}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* FBO - Airport Selection Step */}
                    {locationType === 'fbo' && !selectedAirport && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">
                          Step 1: Search for airport
                        </p>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              handleLocationSearch(e.target.value);
                            }}
                            placeholder="Search airport..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent"
                            autoFocus
                          />
                          {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted animate-spin" />
                          )}
                        </div>
                      </div>
                    )}

                    {/* FBO - FBO Selection Step (after airport is selected) */}
                    {locationType === 'fbo' && selectedAirport && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 p-2 bg-ui-blue-bg rounded-lg">
                          <Plane className="w-4 h-4 text-ui-link" />
                          <span className="text-xs text-ui-blue-fg font-medium">{selectedAirport.name}</span>
                        </div>
                        <p className="text-xs font-medium text-ui-ink-3">
                          Step 2: Search for FBO
                        </p>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              handleLocationSearch(e.target.value);
                            }}
                            placeholder="Search FBO..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent"
                            autoFocus
                          />
                          {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted animate-spin" />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Search Input for Airport, Train Station (uses Urbanride API) */}
                    {locationType && locationType !== 'fbo' && locationType !== 'address' && locationType !== 'favorites' && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">
                          Search for {locationType === 'airport' ? 'an airport' : 'a train station'}:
                        </p>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              handleLocationSearch(e.target.value);
                            }}
                            placeholder={
                              locationType === 'airport' ? 'Airport name or code...' : 'Station name...'
                            }
                            className="w-full pl-9 pr-3 py-2 text-sm border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent"
                            autoFocus
                          />
                          {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted animate-spin" />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Address Search (uses backend Google Places proxy) */}
                    {locationType === 'address' && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">
                          Search for an address:
                        </p>
                        <div className="relative">
                          <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted" />
                          <input
                            type="text"
                            value={addressQuery}
                            onChange={(e) => {
                              setAddressQuery(e.target.value);
                              handleAddressSearch(e.target.value);
                            }}
                            placeholder="Start typing an address..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent"
                            autoFocus
                          />
                          {isAddressSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted animate-spin" />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Address Search Results */}
                    {locationType === 'address' && addressResults.length > 0 && (
                      <div className="max-h-48 overflow-y-auto border border-ui-line rounded-lg divide-y divide-ui-divider">
                        {addressResults.map((address, index) => (
                          <button
                            key={address.id || index}
                            onClick={() => handleSelectAddress(address)}
                            className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-ui-hover transition-colors"
                          >
                            <div className="flex-shrink-0 w-7 h-7 bg-ui-teal-bg rounded-full flex items-center justify-center mt-0.5">
                              <Home className="w-3.5 h-3.5 text-ui-teal-fg" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-ui-ink truncate">
                                {address.name}
                              </p>
                              {address.details && (
                                <p className="text-xs text-ui-muted truncate">
                                  {address.details}
                                </p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Address No Results Message */}
                    {locationType === 'address' && addressQuery.length >= 3 && !isAddressSearching && addressResults.length === 0 && (
                      <div className="text-center py-4">
                        <Home className="w-8 h-8 text-ui-faint mx-auto mb-2" />
                        <p className="text-xs text-ui-muted">No addresses found for "{addressQuery}"</p>
                      </div>
                    )}

                    {/* Address Hint Text */}
                    {locationType === 'address' && addressQuery.length < 3 && !isAddressSearching && (
                      <p className="text-xs text-ui-muted text-center py-2">
                        Type at least 3 characters to search
                      </p>
                    )}

                    {/* Search Results (for non-address types using Urbanride API) */}
                    {locationType && locationType !== 'address' && locationType !== 'favorites' && searchResults.length > 0 && (
                      <div className="max-h-48 overflow-y-auto border border-ui-line rounded-lg divide-y divide-ui-divider">
                        {searchResults.map((location, index) => {
                          const LocationIcon =
                            location.type === 'airport' ? Plane :
                            location.type === 'fbo' ? Building :
                            location.type === 'train' ? Train : MapPin;

                          return (
                            <button
                              key={location.id || index}
                              onClick={() => handleSelectLocation(location)}
                              className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-ui-hover transition-colors"
                            >
                              <div className="flex-shrink-0 w-7 h-7 bg-ui-hover rounded-full flex items-center justify-center mt-0.5">
                                <LocationIcon className="w-3.5 h-3.5 text-ui-ink-3" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-ui-ink truncate">
                                  {location.name}
                                </p>
                                {location.details && (
                                  <p className="text-xs text-ui-muted truncate">
                                    {location.details}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* No Results Message (for non-address types) */}
                    {locationType && locationType !== 'address' && locationType !== 'favorites' && searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
                      <div className="text-center py-4">
                        <MapPin className="w-8 h-8 text-ui-faint mx-auto mb-2" />
                        <p className="text-xs text-ui-muted">No locations found for "{searchQuery}"</p>
                      </div>
                    )}

                    {/* Hint Text (for non-address types) */}
                    {locationType && locationType !== 'address' && locationType !== 'favorites' && searchQuery.length < 2 && !isSearching && (
                      <p className="text-xs text-ui-muted text-center py-2">
                        Type at least 2 characters to search
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Instructions */}
            <div className="text-xs text-ui-muted bg-ui-sunk p-2 rounded-lg">
              <p className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                First item = Pickup, Last item = Dropoff
              </p>
              {orderedComponents.some(c => !c.isFixed) && (
                <p className="flex items-center gap-1 mt-1">
                  <GripVertical className="w-3 h-3" />
                  TBD items can be reordered
                </p>
              )}
            </div>

            {/* Itinerary Items */}
            <div className="space-y-2">
              {orderedComponents.map((component, index) => {
                const role = getItemRole(index, orderedComponents.length);
                const locationName = getLocationName(component, role);
                const locationDetails = getLocationDetails(component);
                const dateTime = getDateTime(component, role);
                const typeColor = getTypeColor(component);
                const canMoveUp = index > 0 && !component.isFixed && !orderedComponents[index - 1]?.isFixed;
                const canMoveDown = index < orderedComponents.length - 1 && !component.isFixed && !orderedComponents[index + 1]?.isFixed;

                return (
                  <motion.div
                    key={`${component.id}-${component.selectionIndex}`}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`relative p-3 rounded-lg border ${
                      role === 'pickup'
                        ? 'border-ui-green-line bg-ui-green-bg/50'
                        : role === 'dropoff'
                          ? 'border-ui-red-line bg-ui-red-bg/50'
                          : 'border-ui-line bg-ui-sunk/50'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {/* Drag handle for TBD items */}
                      <div className="flex flex-col items-center gap-0.5 pt-1">
                        {!component.isFixed && (
                          <>
                            <button
                              onClick={() => moveItem(index, 'up')}
                              disabled={!canMoveUp}
                              className={`p-0.5 rounded ${canMoveUp ? 'text-ui-muted hover:text-ui-ink-3 hover:bg-ui-line' : 'text-ui-faint cursor-not-allowed'}`}
                            >
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <GripVertical className="w-3 h-3 text-ui-faint" />
                            <button
                              onClick={() => moveItem(index, 'down')}
                              disabled={!canMoveDown}
                              className={`p-0.5 rounded ${canMoveDown ? 'text-ui-muted hover:text-ui-ink-3 hover:bg-ui-line' : 'text-ui-faint cursor-not-allowed'}`}
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </>
                        )}
                        {component.isFixed && (
                          <div className="w-5 h-5 flex items-center justify-center">
                            <Clock className="w-3 h-3 text-ui-muted" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getRoleBadge(role, index)}
                          {component.isFixed && (
                            <span className="text-[10px] text-ui-muted bg-ui-line px-1.5 py-0.5 rounded">Fixed</span>
                          )}
                          {!component.isFixed && (
                            <span className="text-[10px] text-ui-orange-fg bg-ui-orange-bg px-1.5 py-0.5 rounded">TBD</span>
                          )}
                        </div>
                        <p className={`text-sm font-medium ${typeColor} truncate`}>
                          {locationName}
                        </p>
                        {locationDetails && (
                          <p className="text-xs text-ui-muted truncate">
                            {locationDetails}
                          </p>
                        )}
                        {dateTime && (
                          <p className="text-xs text-ui-muted mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {dateTime}
                          </p>
                        )}
                      </div>

                      {/* Remove button */}
                      <button
                        onClick={() => onRemoveComponent(component.selectionIndex)}
                        className="p-1 text-ui-muted hover:text-ui-red-fg hover:bg-ui-red-bg rounded transition-colors"
                        title="Remove from itinerary"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Arrow connector */}
                    {index < orderedComponents.length - 1 && (
                      <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 z-10">
                        <div className="w-6 h-6 rounded-full bg-ui-surface border border-ui-line flex items-center justify-center">
                          <ArrowRight className="w-3 h-3 text-ui-muted rotate-90" />
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Add Manual Location Section */}
            <div className="mt-4 pt-3 border-t border-ui-line">
              <AnimatePresence mode="wait">
                {!showAddLocation ? (
                  <motion.button
                    key="add-button"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowAddLocation(true)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-ui-hover hover:bg-ui-line text-ui-ink-2 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm font-medium">Add Manual Location</span>
                  </motion.button>
                ) : (
                  <motion.div
                    key="add-panel"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-3"
                  >
                    {/* Header with back button */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={handleBackInAddLocation}
                        className="flex items-center gap-1 text-sm text-ui-ink-3 hover:text-ui-ink"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        {selectedAirport ? 'Back to Airport' : locationType ? 'Back' : 'Cancel'}
                      </button>
                      <button
                        onClick={resetAddLocationForm}
                        className="p-1 text-ui-muted hover:text-ui-ink-3"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Location Type Selection */}
                    {!locationType && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">Select location type:</p>
                        <div className="grid grid-cols-2 gap-2">
                          {locationTypes.map((type) => {
                            const IconComponent = type.icon;
                            return (
                              <button
                                key={type.value}
                                onClick={() => {
                                  if (type.value === 'as_directed') {
                                    handleAsDirectedSelection();
                                  } else if (type.value === 'favorites') {
                                    setLocationType('favorites');
                                  } else {
                                    setLocationType(type.value);
                                  }
                                }}
                                className={`flex flex-col items-center gap-1.5 p-3 bg-ui-surface border border-ui-line rounded-lg hover:border-ui-primary hover:bg-ui-blue-bg transition-all ${type.value === 'as_directed' ? 'col-span-2' : ''} ${type.value === 'favorites' ? 'col-span-2 border-ui-amber-line bg-ui-amber-bg/30 hover:border-ui-amber-solid hover:bg-ui-amber-bg' : ''}`}
                              >
                                <IconComponent className={`w-5 h-5 ${type.value === 'favorites' ? 'text-ui-amber-fg' : 'text-ui-ink-3'}`} />
                                <span className={`text-xs font-medium ${type.value === 'favorites' ? 'text-ui-amber-fg' : 'text-ui-ink-2'}`}>{type.label}</span>
                                {(type.value === 'as_directed' || type.value === 'favorites') && (
                                  <span className="text-[10px] text-ui-muted">{type.description}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Favorites Selection */}
                    {locationType === 'favorites' && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">Select from saved addresses:</p>
                        <div className="max-h-48 overflow-y-auto border border-ui-line rounded-lg divide-y divide-ui-divider">
                          {favoriteAddresses.map((favorite, index) => {
                            const addr = favorite.address || {};
                            return (
                              <button
                                key={favorite.id || index}
                                onClick={() => handleSelectFavorite(favorite)}
                                className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-ui-amber-bg transition-colors"
                              >
                                <div className="flex-shrink-0 w-7 h-7 bg-ui-amber-bg rounded-full flex items-center justify-center mt-0.5">
                                  <Star className="w-3.5 h-3.5 text-ui-amber-fg" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-ui-ink truncate">
                                    {favorite.label}
                                  </p>
                                  <p className="text-xs text-ui-muted truncate">
                                    {addr.addressLine1}
                                    {addr.city && `, ${addr.city}`}
                                  </p>
                                  <p className="text-[10px] text-ui-muted truncate">
                                    {favorite.executiveName}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* FBO - Airport Selection Step */}
                    {locationType === 'fbo' && !selectedAirport && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">
                          Step 1: Search for airport
                        </p>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              handleLocationSearch(e.target.value);
                            }}
                            placeholder="Search airport..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent"
                            autoFocus
                          />
                          {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted animate-spin" />
                          )}
                        </div>
                      </div>
                    )}

                    {/* FBO - FBO Selection Step (after airport is selected) */}
                    {locationType === 'fbo' && selectedAirport && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 p-2 bg-ui-blue-bg rounded-lg">
                          <Plane className="w-4 h-4 text-ui-link" />
                          <span className="text-xs text-ui-blue-fg font-medium">{selectedAirport.name}</span>
                        </div>
                        <p className="text-xs font-medium text-ui-ink-3">
                          Step 2: Search for FBO
                        </p>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              handleLocationSearch(e.target.value);
                            }}
                            placeholder="Search FBO..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent"
                            autoFocus
                          />
                          {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted animate-spin" />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Search Input for Airport, Train Station (uses Urbanride API) */}
                    {locationType && locationType !== 'fbo' && locationType !== 'address' && locationType !== 'favorites' && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">
                          Search for {locationType === 'airport' ? 'an airport' : 'a train station'}:
                        </p>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              handleLocationSearch(e.target.value);
                            }}
                            placeholder={
                              locationType === 'airport' ? 'Airport name or code...' : 'Station name...'
                            }
                            className="w-full pl-9 pr-3 py-2 text-sm border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent"
                            autoFocus
                          />
                          {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted animate-spin" />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Address Search (uses backend Google Places proxy) */}
                    {locationType === 'address' && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-ui-ink-3">
                          Search for an address:
                        </p>
                        <div className="relative">
                          <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted" />
                          <input
                            type="text"
                            value={addressQuery}
                            onChange={(e) => {
                              setAddressQuery(e.target.value);
                              handleAddressSearch(e.target.value);
                            }}
                            placeholder="Start typing an address..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-ui-line rounded-lg focus:outline-none focus:ring-2 focus:ring-ui-primary focus:border-transparent"
                            autoFocus
                          />
                          {isAddressSearching && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-muted animate-spin" />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Address Search Results */}
                    {locationType === 'address' && addressResults.length > 0 && (
                      <div className="max-h-48 overflow-y-auto border border-ui-line rounded-lg divide-y divide-ui-divider">
                        {addressResults.map((address, index) => (
                          <button
                            key={address.id || index}
                            onClick={() => handleSelectAddress(address)}
                            className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-ui-hover transition-colors"
                          >
                            <div className="flex-shrink-0 w-7 h-7 bg-ui-teal-bg rounded-full flex items-center justify-center mt-0.5">
                              <Home className="w-3.5 h-3.5 text-ui-teal-fg" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-ui-ink truncate">
                                {address.name}
                              </p>
                              {address.details && (
                                <p className="text-xs text-ui-muted truncate">
                                  {address.details}
                                </p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Address No Results Message */}
                    {locationType === 'address' && addressQuery.length >= 3 && !isAddressSearching && addressResults.length === 0 && (
                      <div className="text-center py-4">
                        <Home className="w-8 h-8 text-ui-faint mx-auto mb-2" />
                        <p className="text-xs text-ui-muted">No addresses found for "{addressQuery}"</p>
                      </div>
                    )}

                    {/* Address Hint Text */}
                    {locationType === 'address' && addressQuery.length < 3 && !isAddressSearching && (
                      <p className="text-xs text-ui-muted text-center py-2">
                        Type at least 3 characters to search
                      </p>
                    )}

                    {/* Search Results (for non-address types using Urbanride API) */}
                    {locationType && locationType !== 'address' && locationType !== 'favorites' && searchResults.length > 0 && (
                      <div className="max-h-48 overflow-y-auto border border-ui-line rounded-lg divide-y divide-ui-divider">
                        {searchResults.map((location, index) => {
                          const LocationIcon =
                            location.type === 'airport' ? Plane :
                            location.type === 'fbo' ? Building :
                            location.type === 'train' ? Train : MapPin;

                          return (
                            <button
                              key={location.id || index}
                              onClick={() => handleSelectLocation(location)}
                              className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-ui-hover transition-colors"
                            >
                              <div className="flex-shrink-0 w-7 h-7 bg-ui-hover rounded-full flex items-center justify-center mt-0.5">
                                <LocationIcon className="w-3.5 h-3.5 text-ui-ink-3" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-ui-ink truncate">
                                  {location.name}
                                </p>
                                {location.details && (
                                  <p className="text-xs text-ui-muted truncate">
                                    {location.details}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* No Results Message (for non-address types) */}
                    {locationType && locationType !== 'address' && locationType !== 'favorites' && searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
                      <div className="text-center py-4">
                        <MapPin className="w-8 h-8 text-ui-faint mx-auto mb-2" />
                        <p className="text-xs text-ui-muted">No locations found for "{searchQuery}"</p>
                      </div>
                    )}

                    {/* Hint Text (for non-address types) */}
                    {locationType && locationType !== 'address' && locationType !== 'favorites' && searchQuery.length < 2 && !isSearching && (
                      <p className="text-xs text-ui-muted text-center py-2">
                        Type at least 2 characters to search
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="px-4 py-3 border-t border-ui-line bg-ui-sunk rounded-b-xl">
        {selectedComponents.length < 2 ? (
          <div className="flex items-center gap-2 text-xs text-ui-amber-fg bg-ui-amber-bg px-3 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Select at least 2 locations to book a transfer</span>
          </div>
        ) : (
          <Button
            onClick={() => onStartBooking()}
            className="w-full bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary font-medium py-2.5"
          >
            <Car className="w-4 h-4 mr-2" />
            Book Urbanride
          </Button>
        )}
      </div>
    </motion.div>
  );
};

export default UrbanrideBookingSummary;
